// Saved filter sets for a dashboard: load, apply, save and delete favourite combinations.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { loadFilterSets, saveFilterSet, deleteFilterSet, setsForTab } from "@/lib/filterSets";

export function useFilterSets({ dashboardId, publicToken = null, tabId = null, values = {}, onApply, enabled = true }) {
  const [sets, setSets] = useState([]);

  useEffect(() => {
    if (!enabled || (!dashboardId && !publicToken)) return undefined;
    let active = true;
    loadFilterSets({ dashboardId, publicToken })
      .then((s) => { if (active) setSets(s); })
      .catch(() => { if (active) setSets([]); });
    return () => { active = false; };
  }, [dashboardId, publicToken, enabled]);

  const applySet = useCallback((s) => {
    onApply?.(s?.values || {});
    toast.success(`Applied “${s?.name}”`);
  }, [onApply]);

  const saveSet = useCallback(async ({ name, shared }) => {
    const clean = Object.fromEntries(Object.entries(values || []).filter(
      ([, v]) => v !== undefined && v !== null && v !== "" && !(Array.isArray(v) && v.length === 0)));
    try {
      const saved = await saveFilterSet({ dashboardId, publicToken, name, values: clean, tabId, shared });
      setSets((prev) => [...prev.filter((s) => s.id !== saved.id), { ...saved, mine: true }]);
      toast.success(`Saved “${name}”`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save this filter set");
    }
  }, [dashboardId, publicToken, tabId, values]);

  const deleteSet = useCallback(async (s) => {
    try {
      await deleteFilterSet({ dashboardId, publicToken, id: s.id });
      setSets((prev) => prev.filter((x) => x.id !== s.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete this filter set");
    }
  }, [dashboardId, publicToken]);

  return { sets: setsForTab(sets, tabId), applySet, saveSet, deleteSet };
}
